import { createElement, type ComponentType } from 'react'
import type { TFunction } from 'i18next'
import { Archive, ArchiveRestore, Star } from 'lucide-react'
import { showSuccessToast } from '@/src/renderer/components/common/SuccessToast'
import { showUndoToast } from '@/src/renderer/components/common/UndoToast'
import { showErrorToast, showInfoToast } from '@/src/renderer/components/common/MessageToast'
import { DormantIcon, RecurringIcon } from '@/src/renderer/components/icons'
import { cascadeDescription } from '@/src/renderer/features/taskManagement'

/** Kurz nacheinander, damit sich der Stapel sichtbar aufbaut. */
const STAGGER_MS = 350

/**
 * **Hilfe › Alle Toasts zeigen** — nur in der Entwicklung (`menuService`, nicht gepackt; #223,
 * Sven 04.10.2026). Jede Art und jede Zeichenfarbe einmal, mit den echten Texten der App — auch der
 * zweiten Zeile. Die Rückgängig-Knöpfe tun nichts; die Aufgaben-Titel sind Beispiele.
 */
export function showAllToastsForDev(t: TFunction, locale: string): void {
  const icon = (component: ComponentType<{ size?: number }>) => createElement(component, { size: 18 })
  const undo = { undoLabel: t('common.undo'), onUndo: () => {} }
  const toasts: Array<() => void> = [
    () => showSuccessToast({ message: t('dormant.menu.toastWakeInProject'), description: 'Fliesen aussuchen', action: { label: t('dormant.menu.toastOpenProject', { project: 'Umzug' }), onClick: () => {} } }),
    () => showSuccessToast({ message: t('recurrence.comeback.tomorrow'), description: 'Blumen gießen', icon: icon(RecurringIcon), tone: 'recurring' }),
    () => showSuccessToast({ message: t('taskItem.pausedToast'), description: t('taskItem.pausedToastText', { count: 1 }), icon: icon(DormantIcon), tone: 'rest' }),
    () => showSuccessToast({ message: t('section.unarchived', { title: 'Einkauf' }), description: t('section.unarchivedTasksText'), icon: icon(ArchiveRestore), tone: 'success' }),
    () => showUndoToast({
      ...undo,
      message: t('taskItem.deleted', { title: 'Regal bauen' }),
      description: cascadeDescription({ subtaskIds: ['a', 'b', 'c'], unlinkedNoteIds: [], timeEntryIds: [], commentIds: ['k'], reminderIds: ['r'] }, t, locale),
      undoneMessage: t('undo.restored.task', { title: 'Regal bauen' }),
    }),
    () => showUndoToast({ ...undo, message: t('favorites.added'), description: t('favorites.addedText'), undoneMessage: t('favorites.undoneAdd'), icon: icon(Star), tone: 'favorite' }),
    () => showUndoToast({ ...undo, message: t('section.archived', { title: 'Einkauf' }), description: t('section.archivedClosed', { count: 3 }), undoneMessage: t('section.unarchived', { title: 'Einkauf' }), icon: icon(Archive), tone: 'archive' }),
    () => showInfoToast(t('taskList.nest.refusedTitle'), { description: t('taskList.nest.recurringToast') }),
    () => showErrorToast(t('sync.resumeFailedTitle'), { description: t('sync.resumeFailedText') }),
  ]
  toasts.forEach((show, i) => setTimeout(show, i * STAGGER_MS))
}
